import { useCallback, useState } from 'react';
import { getAccessToken } from '../services/client';
import * as wallet from '../services/wallet';
import useAsync, { Async } from './useAsync';

/**
 * The coin balance, and the statement behind it.
 *
 * Two requests rather than one: the summary is small and drives the header, the
 * statement is paged and drives the list. Keeping them apart means a long
 * statement never delays the balance - the number the screen exists to show
 * lands first and renders on its own.
 */
export type UseWallet = {
  summary: Async<wallet.WalletSummary>;
  statement: Async<wallet.WalletStatement>;
  /** Rows loaded so far, across every page fetched. */
  transactions: wallet.WalletTransaction[];
  /** True while a `loadMore()` is in flight. Distinct from a first load. */
  loadingMore: boolean;
  /** False once the server stops handing back a cursor. */
  hasMore: boolean;
  loadMore: () => void;
  /** Re-read both, from the top. Safe for pull-to-refresh. */
  reload: () => void;
  /** False when there is no session - nothing was asked for, so nothing failed. */
  signedIn: boolean;
};

export function useWallet(): UseWallet {
  /*
   * Both endpoints are behind the customer guard, so with no session they
   * answer 401 and `useAsync` reports an ordinary error - which this screen
   * then drew as a bare "-" with no way to act on it.
   *
   * Not reactive, for the reason `DriverCard` gives: signing in re-renders
   * this tree from `HomeScreen`, which is when the answer changes.
   */
  const signedIn = !!getAccessToken();

  const summary = useAsync(() => wallet.summary(), [], { enabled: signedIn });
  const statement = useAsync(() => wallet.transactions({ limit: 25 }), [], {
    enabled: signedIn,
  });

  /*
   * Pages beyond the first, kept separate from `statement.data`.
   *
   * Appending into the hook's own state would be overwritten the moment
   * `useAsync` refetches page one; holding the extra pages here means a
   * refresh correctly collapses back to a single page instead of showing the
   * first page twice.
   */
  const [extra, setExtra] = useState<wallet.WalletTransaction[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const firstPage = statement.data?.items ?? [];
  const nextCursor = cursor ?? statement.data?.nextCursor ?? null;

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMore) {
      return;
    }
    setLoadingMore(true);
    wallet
      .transactions({ limit: 25, cursor: nextCursor })
      .then(page => {
        setExtra(current => [...current, ...page.items]);
        setCursor(page.nextCursor);
      })
      .catch(() => {
        /*
         * A failed *next* page is deliberately silent. The rows already on
         * screen are still true, and turning a pagination hiccup into a
         * full-screen error would throw away a statement the customer is
         * reading. The button simply stays available to try again.
         */
      })
      .finally(() => setLoadingMore(false));
  }, [nextCursor, loadingMore]);

  const reload = useCallback(() => {
    setExtra([]);
    setCursor(null);
    summary.reload();
    statement.reload();
  }, [summary, statement]);

  return {
    summary,
    statement,
    transactions: [...firstPage, ...extra],
    loadingMore,
    hasMore: nextCursor !== null,
    loadMore,
    reload,
    signedIn,
  };
}

export default useWallet;
